import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import MemberDashboard from './pages/MemberDashboard';
import { ThemeProvider, createTheme } from '@mui/material';
import './styles/global.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0066cc',
    },
    secondary: {
      main: '#424245',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#171717',
      secondary: '#424245',
    },
  },
  breakpoints: {
    values: {
      xs: 0,    // Mobile phones
      sm: 600,  // Tablets
      md: 900,  // Small laptops
      lg: 1200, // Desktops
      xl: 1536  // Large screens
    }
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro", system-ui, sans-serif',
    h1: {
      fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      fontWeight: 600,
      fontSize: '40px',
      letterSpacing: '-0.015em',
      lineHeight: 1.1,
      '@media (max-width:600px)': {
        fontSize: '32px',
      },
    },
    h2: {
      fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      fontWeight: 600,
      fontSize: '32px',
      letterSpacing: '-0.015em',
      lineHeight: 1.2,
      '@media (max-width:600px)': {
        fontSize: '28px',
      },
    },
    h3: {
      fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      fontWeight: 600,
      fontSize: '24px',
      letterSpacing: '-0.015em',
      lineHeight: 1.3,
      '@media (max-width:600px)': {
        fontSize: '20px',
      },
    },
    h4: {
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h6: {
      fontSize: '1.1rem',
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    body1: {
      fontSize: '17px',
      letterSpacing: '-0.01em',
      lineHeight: 1.47059,
      '@media (max-width:600px)': {
        fontSize: '16px',
      },
    },
    body2: {
      fontSize: '15px',
      letterSpacing: '-0.01em',
      lineHeight: 1.47059,
      '@media (max-width:600px)': {
        fontSize: '14px',
      },
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '24px',
          paddingRight: '24px',
          '@media (max-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          fontWeight: 500,
          fontSize: '15px',
          borderRadius: '8px',
          textTransform: 'none',
          padding: '8px 16px',
          transition: 'all 0.2s ease',
          '&:hover': {
            opacity: 0.9,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
          '@media (max-width:600px)': {
            borderRadius: '8px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
            fontSize: '15px',
            '& fieldset': {
              borderColor: '#d2d2d7',
            },
            '&:hover fieldset': {
              borderColor: '#0066cc',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0066cc',
              boxShadow: '0 0 0 2px rgba(0, 102, 204, 0.1)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          '@media (max-width:600px)': {
            borderRadius: '8px',
            margin: '16px',
          },
        },
      },
    },
  },
});

const PrivateRoute = ({ children, allowedRoles }) => {
  const user = JSON.parse(localStorage.getItem('user'));
  const token = localStorage.getItem('token');

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/member'} replace />;
  }

  return children;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5'
        }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route path="/admin" element={
            <PrivateRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </PrivateRoute>
          } />
          
          <Route path="/member" element={
            <PrivateRoute allowedRoles={['member', 'admin']}>
              <MemberDashboard />
            </PrivateRoute>
          } />
          
          <Route path="/" element={
            <Navigate to="/login" replace />
          } />

          {/* Catch all route */}
          <Route path="*" element={
            <Navigate to="/login" replace />
          } />
        </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;
